// src/shared/stores/useUserStore.ts
import { create } from 'zustand';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/config';
import { getAndSanitize } from '../../shared/utils/sanitization';

interface User {
  id: string;
  username: string;
  role: string;
  active: boolean;
  awaitingAccess?: boolean;
  panels?: Array<{ id: string; name?: string; isActive?: boolean }>;
  wallets?: Array<{ id: string; name: string }>;
  // Other user properties
}

interface UserState {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  lastFetched: number | null;
  fetchUser: (userId: string, forceRefresh?: boolean) => Promise<User | null>;
  setUser: (user: User | null) => void;
  clearUser: () => void;
}

// Cache time: 5 minutes
const CACHE_TIME = 5 * 60 * 1000;

// Track ongoing requests to prevent duplicates
const currentUserFetch: Record<string, Promise<User>> = {};

export const useUserStore = create<UserState>((set, get) => ({
  user: null,
  isLoading: false,
  error: null,
  lastFetched: null,

  fetchUser: async (userId: string, forceRefresh = false) => {
    const { user, lastFetched } = get();
    const currentTime = Date.now();
    
    // Return cached data if fresh
    if (
      user && 
      user.id === userId && 
      lastFetched && 
      currentTime - lastFetched < CACHE_TIME && 
      !forceRefresh
    ) {
      return user;
    }
    
    // Reuse in-flight request if exists
    if (currentUserFetch[userId]) {
      return currentUserFetch[userId];
    }
    
    set({ isLoading: true, error: null });
    
    try {
      // Store promise to deduplicate concurrent requests
      currentUserFetch[userId] = new Promise(async (resolve, reject) => {
        try {
          const token = getAndSanitize('token');


          const authHeader = `Bearer ${token}`;

          const response = await axios.get(`${API_BASE_URL}/api/auth/users/${userId}`, {
            headers: { Authorization: authHeader }
          });


          const userData = response.data.data;

          set({
            user: userData,
            isLoading: false,
            lastFetched: Date.now()
          });

          resolve(userData);
        } catch (error) {
          console.error('[UserStore] ❌ Fetch failed with error:', error);
          if (axios.isAxiosError(error)) {
            console.error('[UserStore] Axios error details:', {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              headers: error.config?.headers
            });
          }
          const typedError = error as Error;
          set({ isLoading: false, error: typedError });
          reject(typedError);
        } finally {
          // Clean up tracked request
          delete currentUserFetch[userId];
        }
      });

      return await currentUserFetch[userId];
    } catch (error) {
      console.error('[UserStore] Error fetching user (outer catch):', error);
      return null;
    }
  },
  getAdminId: (): string | null => {
  // Try from localStorage first
  const storedAdminId = getAndSanitize("adminId");
  if (storedAdminId) return storedAdminId;
  
  // Try from user ID
  const userId = getAndSanitize("userId");
  if (userId) return userId;
  
  // Try from token
  const token = getAndSanitize('token');
  if (!token) return null;
  
  try {
    const tokenData = JSON.parse(atob(token.split('.')[1]));
    return tokenData.userId || null;
  } catch (error) {
    console.error('[UserStore] Error extracting admin ID from token:', error);
    return null;
  }
},
  setUser: (user) => set({ user, lastFetched: Date.now() }),
  
  clearUser: () => set({ user: null, lastFetched: null }),
}));